import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { getNetworkReconnectionManager } from '../utils/networkReconnectionManager';

// Connection states
export type ConnectionState = 'connected' | 'connecting' | 'disconnected' | 'reconnecting';

// Connection status data structure
export interface ConnectionStatus {
  state: ConnectionState;
  isConnected: boolean;
  lastHeartbeat: Date | null;
  reconnectAttempts: number;
  lastError: string | null;
}

// Context interface
interface ConnectionStatusContextType {
  status: ConnectionStatus;
  updateConnectionState: (state: ConnectionState) => void;
  recordHeartbeat: () => void;
  recordError: (error: string) => void;
  incrementReconnectAttempts: () => void;
  resetReconnectAttempts: () => void;
}

// Default status
const defaultStatus: ConnectionStatus = {
  state: 'connecting',
  isConnected: false,
  lastHeartbeat: null,
  reconnectAttempts: 0,
  lastError: null,
};

const ConnectionStatusContext = createContext<ConnectionStatusContextType | undefined>(undefined);

// Custom hook to use connection status
export const useConnectionStatus = () => {
  const context = useContext(ConnectionStatusContext);
  if (!context) {
    throw new Error('useConnectionStatus must be used within ConnectionStatusProvider');
  }
  return context;
};

// Optional hook that returns null if not in provider (for components that may be outside provider)
export const useConnectionStatusOptional = () => {
  return useContext(ConnectionStatusContext);
};

// Sound utility for connection state changes
const playConnectionSound = (type: 'connect' | 'disconnect') => {
  try {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    if (type === 'connect') {
      // Pleasant ascending tone for connection
      oscillator.frequency.setValueAtTime(440, audioContext.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(880, audioContext.currentTime + 0.15);
      gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
    } else {
      // Descending tone for disconnection
      oscillator.frequency.setValueAtTime(660, audioContext.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(330, audioContext.currentTime + 0.2);
      gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.25);
    }

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.3);
  } catch (e) {
    // Silently fail if audio is not supported
    console.debug('Audio notification not available:', e);
  }
};

interface ConnectionStatusProviderProps {
  children: React.ReactNode;
}

export const ConnectionStatusProvider: React.FC<ConnectionStatusProviderProps> = ({ children }) => {
  const [status, setStatus] = useState<ConnectionStatus>(defaultStatus);
  const previousStateRef = useRef<ConnectionState>('connecting');

  // Play sound on state changes
  useEffect(() => {
    const prevState = previousStateRef.current;
    const newState = status.state;

    // Only play sounds on meaningful transitions
    if (prevState !== newState) {
      if (newState === 'connected' && (prevState === 'disconnected' || prevState === 'reconnecting')) {
        playConnectionSound('connect');
      } else if (newState === 'disconnected' && prevState === 'connected') {
        playConnectionSound('disconnect');
      }
      previousStateRef.current = newState;
    }
  }, [status.state]);

  // Subscribe to NetworkReconnectionManager events
  useEffect(() => {
    const manager = getNetworkReconnectionManager();

    const handleConnectionLost = () => {
      setStatus(prev => ({
        ...prev,
        state: 'disconnected',
        isConnected: false,
        lastError: 'Connection lost'
      }));
    };

    const handleConnectionRestored = () => {
      setStatus(prev => ({
        ...prev,
        state: 'connected',
        isConnected: true,
        lastHeartbeat: new Date(),
        lastError: null,
        reconnectAttempts: 0
      }));
    };

    const handleScheduleRetry = () => {
      setStatus(prev => ({
        ...prev,
        state: 'reconnecting',
        isConnected: false,
        reconnectAttempts: prev.reconnectAttempts + 1
      }));
    };

    // Subscribe to events
    manager.on('connectionLost', handleConnectionLost);
    manager.on('connectionRestored', handleConnectionRestored);
    manager.on('scheduleRetry', handleScheduleRetry);

    return () => {
      manager.off('connectionLost', handleConnectionLost);
      manager.off('connectionRestored', handleConnectionRestored);
      manager.off('scheduleRetry', handleScheduleRetry);
    };
  }, []);

  const updateConnectionState = useCallback((state: ConnectionState) => {
    setStatus(prev => {
      const isConnected = state === 'connected';
      // Skip re-render if state hasn't actually changed
      if (prev.state === state && prev.isConnected === isConnected) return prev;
      return {
        ...prev,
        state,
        isConnected,
        lastError: state === 'connected' ? null : prev.lastError,
      };
    });
  }, []);

  const recordHeartbeat = useCallback(() => {
    setStatus(prev => {
      const now = Date.now();
      const lastHbAge = prev.lastHeartbeat ? now - prev.lastHeartbeat.getTime() : Infinity;
      // If already connected, heartbeat is fresh (<10s old), and counts are clean → skip re-render
      if (
        prev.state === 'connected' &&
        prev.isConnected &&
        lastHbAge < 10000 &&
        prev.reconnectAttempts === 0 &&
        !prev.lastError
      ) {
        return prev;
      }
      return {
        ...prev,
        lastHeartbeat: new Date(),
        state: 'connected',
        isConnected: true,
        lastError: null,
        reconnectAttempts: 0,
      };
    });
  }, []);

  const recordError = useCallback((error: string) => {
    setStatus(prev => ({
      ...prev,
      lastError: error,
    }));
  }, []);

  const incrementReconnectAttempts = useCallback(() => {
    setStatus(prev => ({
      ...prev,
      reconnectAttempts: prev.reconnectAttempts + 1,
      state: 'reconnecting',
      isConnected: false,
    }));
  }, []);

  const resetReconnectAttempts = useCallback(() => {
    setStatus(prev => ({
      ...prev,
      reconnectAttempts: 0,
    }));
  }, []);

  const value: ConnectionStatusContextType = {
    status,
    updateConnectionState,
    recordHeartbeat,
    recordError,
    incrementReconnectAttempts,
    resetReconnectAttempts,
  };

  return (
    <ConnectionStatusContext.Provider value={value}>
      {children}
    </ConnectionStatusContext.Provider>
  );
};

export default ConnectionStatusContext;
